import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Modal from '../secondary/Modal';
import { mostrarInfo } from '../../utils/Alertas';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material'
import './css/CalculadoraForex.css';

const CalculadoraForex: React.FC = () => {
    const [tipoOperacion, setTipoOperacion] = useState<string>('compra');
    const [precioXAUUSD, setPrecioXAUUSD] = useState<string>('0');
    const [tamañoCuenta, setTamañoCuenta] = useState<string>('0');
    const [lotajeEntrada, setLotajeEntrada] = useState<string>('0');
    const [stopLoss, setStopLoss] = useState<string>('0');
    const [takeProfit, setTakeProfit] = useState<string>('0');
    const [dolaresArriesgados, setDolaresArriesgados] = useState<number | null>(null);
    const [dolaresBeneficio, setDolaresBeneficio] = useState<number | null>(null);
    const [pipsPerdidas, setPipsPerdidas] = useState<number>(0);
    const [pipsGanancias, setPipsGanancias] = useState<number>(0);
    const [tipoCambio, setTipoCambio] = useState<number>(1);
    const [modalLotajeMaximo, setModalLotajeMaximo] = useState<boolean>(false);
    const [apalancamiento, setApalancamiento] = useState<string>('');
    const [resultadoLotajeMaximo, setResultadoLotajeMaximo] = useState<number | null>(null);

    const apiKey = "ba76251fae801e6ed85976605a0a4cf3";

    // Función para obtener el tipo de cambio al calcular
    const obtenerTipoCambio = async () => {
        const cachedData = localStorage.getItem('tipoCambioData');
        if (cachedData) {
            const { tipoCambio, timestamp } = JSON.parse(cachedData);
            const ahora = new Date().getTime();
            const doceHoras = 12 * 60 * 60 * 1000; // 12 horas en milisegundos

            if (ahora - timestamp < doceHoras) {
                setTipoCambio(tipoCambio);
                return tipoCambio;
            }
        }

        try {
            const response = await axios.get(
              `http://apilayer.net/api/live?access_key=${apiKey}&currencies=EUR&source=USD&format=1`
            );
            const tipoCambioAPI = response.data.quotes.USDEUR;
            setTipoCambio(tipoCambioAPI);

            const tipoCambioData = {
                tipoCambio: tipoCambioAPI,
                timestamp: new Date().getTime()
            };
            localStorage.setItem('tipoCambioData', JSON.stringify(tipoCambioData));
            return tipoCambioAPI;
        } catch (error) {
            console.error("Error obteniendo el tipo de cambio:", error);
            return tipoCambio;
        }
    };

    // Función para calcular los resultados
    const calcularResultados = async () => {
        const precio = Number(precioXAUUSD);
        const cuenta = Number(tamañoCuenta);
        const lotaje = Number(lotajeEntrada);
        const sl = Number(stopLoss);
        const tp = Number(takeProfit);

        let stopLossPips = 0;
        let takeProfitPips = 0;
        let errores = false;

        setDolaresArriesgados(null);
        setDolaresBeneficio(null);
        setPipsPerdidas(0);
        setPipsGanancias(0);

        // Validación básica
        if (
            !tipoOperacion ||
            !precio ||
            !cuenta ||
            !lotaje ||
            !sl ||
            Number.isNaN(precio) ||
            Number.isNaN(cuenta) ||
            Number.isNaN(lotaje) ||
            Number.isNaN(sl)
        ) {
            mostrarInfo('Por favor, informe correctamente todos los campos obligatorios.');
            return;
        }

        // Obtener tipo de cambio
        await obtenerTipoCambio();

        // Validar Stop Loss
        if (tipoOperacion === 'compra') {
            stopLossPips = (precio - sl) / 0.1;
            if (stopLossPips <= 0) {
                mostrarInfo('El stop-loss debe estar por debajo del precio actual para una operación de compra.');
                errores = true;
            }
        } else {
            stopLossPips = (sl - precio) / 0.1;
            if (stopLossPips <= 0) {
                mostrarInfo('El stop-loss debe estar por encima del precio actual para una operación de venta.');
                errores = true;
            }
        }

        // Validar Take Profit (opcional)
        if (!errores && takeProfit && !Number.isNaN(tp)) {
            if (tipoOperacion === 'compra') {
                takeProfitPips = (tp - precio) / 0.1;
                if (takeProfitPips <= 0) {
                    mostrarInfo('El take-profit debe estar por encima del precio actual para una operación de compra.');
                    errores = true;
                }
            } else {
                takeProfitPips = (precio - tp) / 0.1;
                if (takeProfitPips <= 0) {
                    mostrarInfo('El take-profit debe estar por debajo del precio actual para una operación de venta.');
                    errores = true;
                }
            }
        }

        if (errores) return;

        // === CÁLCULOS ===

        // Lotaje real XAUUSD
        const lotajeRealXAUUSD = (lotaje * precio * 100) / 100000;

        // Valor del pip
        const valorPip = (0.1 / precio) * lotajeRealXAUUSD * 100000;

        // Pérdidas
        const riesgoUSD = stopLossPips * valorPip;
        setDolaresArriesgados(riesgoUSD);
        setPipsPerdidas(stopLossPips);

        // Beneficio (si hay TP)
        if (takeProfit && takeProfitPips > 0) {
            const beneficioUSD = takeProfitPips * valorPip;
            setDolaresBeneficio(beneficioUSD);
            setPipsGanancias(takeProfitPips);
        } else {
            setDolaresBeneficio(null);
            setPipsGanancias(0);
        }
    };

    const calcularLotajeMaximo = () => {
        const precio = Number(precioXAUUSD);
        const balance = Number(tamañoCuenta);
        const leverage = Number(apalancamiento);

        let camposFaltantes: string[] = [];

        if (!precio || Number.isNaN(precio)) camposFaltantes.push("Entrada (Precio)");
        if (!balance || Number.isNaN(balance)) camposFaltantes.push("Tamaño cuenta (€)");
        if (!leverage || Number.isNaN(leverage)) camposFaltantes.push("Apalancamiento");

        if (camposFaltantes.length > 0) {
            mostrarInfo(`Debe informar los siguientes campos: ${camposFaltantes.join(", ")}`);
            return;
        }

        const lotaje = (balance * leverage) / (100 * precio);

        setResultadoLotajeMaximo(Number(lotaje.toFixed(3)));
    };

    const resetCampos = () => {
        setPrecioXAUUSD('0');
        setLotajeEntrada('0');
        setStopLoss('0');
        setTakeProfit('0');
        setDolaresArriesgados(null);
        setDolaresBeneficio(null);
        setTamañoCuenta('0');
    };

    // Función para cerrar el modal del resultado
    const cancelModalResultado = () => {
        setDolaresArriesgados(null);
    };

    const handleDecimalChange = (
        value: string,
        setter: React.Dispatch<React.SetStateAction<string>>
    ) => {
        const normalized = value.replace(',', '.');
        if (/^\d*\.?\d*$/.test(normalized)) {
            setter(normalized);
        }
    };

    const tamañoCuentaNum = Number(tamañoCuenta);

    return (
        <div className="container-fluid">
            <div className="text-center">
                <h2>Calculadora Forex</h2>
                <p style={{ fontWeight: "lighter" }}>XAU/USD</p>
            </div>

            <div className="container-fluid content pb-3">
                <form className="forex-form" onSubmit={(e) => { e.preventDefault(); calcularResultados(); }}>
                    <div className="input-group">
                        <FormControl fullWidth  sx={{ mb: 2 }}>
                            <InputLabel id="tipoOperacion-label">Tipo de operación</InputLabel>
                            <Select
                                labelId="tipoOperacion-label"
                                id="tipoOperacion"
                                value={tipoOperacion}
                                onChange={(e: SelectChangeEvent) => setTipoOperacion(e.target.value)}
                                label="Tipo de operación"
                            >
                                <MenuItem value="compra">Compra</MenuItem>
                                <MenuItem value="venta">Venta</MenuItem>
                            </Select>
                        </FormControl>
                    </div>

                    <div className="row">
                        <div className="col-6 ps-0">
                            <div className="input-group">
                                <TextField
                                    label="Entrada (Precio)"
                                    type="text"
                                    inputMode="decimal"
                                    value={precioXAUUSD}
                                    onChange={(e) => handleDecimalChange(e.target.value, setPrecioXAUUSD)}
                                    fullWidth
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-6 pe-0">
                            <div className="input-group">
                                <TextField
                                    label="Tamaño cuenta (€)"
                                    type="text"
                                    inputMode="decimal"
                                    value={tamañoCuenta}
                                    onChange={(e) => handleDecimalChange(e.target.value, setTamañoCuenta)}
                                    fullWidth
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="input-group">
                        <TextField
                            label="Lotaje"
                            type="text"
                            inputMode="decimal"
                            value={lotajeEntrada}
                            onChange={(e) => handleDecimalChange(e.target.value, setLotajeEntrada)}
                            fullWidth
                            required
                            margin="normal"
                            style={{ marginBottom: "16px" }}
                        />
                    </div>

                    <div className="row">
                        <div className="col-6 ps-0">
                            <div className="input-group">
                                <TextField
                                    label="Stop-Loss (Precio)"
                                    type="text"
                                    inputMode="decimal"
                                    value={stopLoss}
                                    onChange={(e) => handleDecimalChange(e.target.value, setStopLoss)}
                                    fullWidth
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-6 pe-0">
                            <div className="input-group">
                                <TextField
                                    label="Take-Profit (Precio)"
                                    type="text"
                                    inputMode="decimal"
                                    value={takeProfit}
                                    onChange={(e) => handleDecimalChange(e.target.value, setTakeProfit)}
                                    fullWidth
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-6 ps-0">
                            <button className="reset-button" type="button" onClick={(e) => { e.preventDefault(); resetCampos(); }}>Reset</button>
                        </div>
                        <div className="col-6 pe-0">
                            <button className="main-button" type="submit">Calcular</button>
                        </div>
                    </div>
                </form>

                <div className="text-center mt-5">
                    <span
                        style={{ cursor: "pointer", textDecoration: "underline", fontWeight: 500 }}
                        onClick={() => {
                            setResultadoLotajeMaximo(null);
                            setApalancamiento('');
                            setModalLotajeMaximo(true);
                        }}
                    >
                        Cálculo lotaje máximo
                    </span>
                </div>

                <div className="text-center mt-3">
                    <Link to="/calculadora-contratos" className="forex-contratos-button">
                        Calculadora Contratos (futuros)
                    </Link>
                </div>

                {dolaresArriesgados !== null && (
                    <Modal onClose={cancelModalResultado}>
                        <div className="modal-body" style={{padding: 0}}>
                            <h2 style={{fontWeight: "bold"}}>Resultado del cálculo</h2>
                            <table className="table forex-result-table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th className="text-center">Monto (€)</th>
                                        <th className="text-center">% de la Cuenta</th>
                                        <th className="text-center">Pips</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dolaresBeneficio !== null && (
                                        <tr>
                                            <td style={{fontWeight: "bold"}}>Ganancias</td>
                                            <td className="text-center">{(dolaresBeneficio * tipoCambio).toFixed(2)}</td>
                                            <td className="text-center">{((dolaresBeneficio * tipoCambio) / tamañoCuentaNum * 100).toFixed(2)}%</td>
                                            <td className="text-center">{pipsGanancias % 1 === 0 ? pipsGanancias : pipsGanancias.toFixed(2)}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td style={{fontWeight: "bold"}}>Pérdidas</td>
                                        <td className="text-center">{(dolaresArriesgados * tipoCambio).toFixed(2)}</td>
                                        <td className="text-center">{((dolaresArriesgados * tipoCambio) / tamañoCuentaNum * 100).toFixed(2)}%</td>
                                        <td className="text-center">{Number.isInteger(pipsPerdidas) ? pipsPerdidas : pipsPerdidas.toFixed(2)}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <p className="text-center" style={{fontWeight: "lighter", margin: 0}}>XAU/USD</p>
                        </div>
                    </Modal>
                )}

                {modalLotajeMaximo && (
                    <Modal onClose={() => setModalLotajeMaximo(false)}>
                        <div className="modal-body">
                            <h2 style={{ fontWeight: "bold" }}>Cálculo lotaje máximo</h2>

                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel id="apalancamiento-label">Apalancamiento</InputLabel>
                                <Select
                                    labelId="apalancamiento-label"
                                    value={apalancamiento}
                                    label="Apalancamiento"
                                    onChange={(e: SelectChangeEvent) => setApalancamiento(e.target.value)}
                                >
                                    <MenuItem value="30">1 : 30</MenuItem>
                                    <MenuItem value="100">1 : 100</MenuItem>
                                    <MenuItem value="200">1 : 200</MenuItem>
                                    <MenuItem value="300">1 : 300</MenuItem>
                                    <MenuItem value="400">1 : 400</MenuItem>
                                    <MenuItem value="500">1 : 500</MenuItem>
                                </Select>
                            </FormControl>

                            <div className="text-center mt-4">
                                <button className="main-button" onClick={calcularLotajeMaximo}>
                                    Calcular
                                </button>
                            </div>

                            {resultadoLotajeMaximo !== null && (
                                <div className="text-center mt-4">
                                    <div
                                        style={{
                                            fontSize: "32px",
                                            fontWeight: "bold",
                                            color: "#1976d2",
                                            letterSpacing: "1px"
                                        }}
                                    >
                                        {resultadoLotajeMaximo.toFixed(3)}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Modal>
                )}

            </div>
        </div>
    );
};

export default CalculadoraForex;
